import React, { useEffect, useState } from "react";
import { Plus, Trash, CircleNotch, Receipt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);
let _uid = 0;
const newItem = () => ({ _uid: `il${++_uid}`, name: "", hsn: "", qty: 1, rate: 0, gst_rate: 18, item_id: "" });

export default function Invoicing({ cid, company }) {
  const [ledgers, setLedgers] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inv, setInv] = useState({ vtype: "Sales", date: today(), number: "", party_ledger_id: "", party_state: company?.state || "", update_stock: true, items: [newItem()] });
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { const [l, s] = await Promise.all([api.get(`/acc/${cid}/ledgers`), api.get(`/acc/${cid}/stock-items`)]); setLedgers(l.data.ledgers); setItems(s.data); } catch (e) { console.error(e); }
    })();
  }, [cid]);

  const partyGroup = inv.vtype === "Sales" || inv.vtype === "Credit Note" ? "Sundry Debtors" : "Sundry Creditors";
  const parties = ledgers.filter((l) => l.group === partyGroup);

  const setLine = (i, k, val) => { const it = [...inv.items]; it[i] = { ...it[i], [k]: val }; setInv({ ...inv, items: it }); };
  const pickItem = (i, iid) => {
    const it = items.find((x) => x.item_id === iid);
    const arr = [...inv.items];
    arr[i] = it ? { ...arr[i], item_id: iid, name: it.name, hsn: it.hsn, gst_rate: it.gst_rate, rate: it.opening_rate || arr[i].rate } : { ...arr[i], item_id: "" };
    setInv({ ...inv, items: arr });
  };
  const addLine = () => setInv({ ...inv, items: [...inv.items, newItem()] });
  const delLine = (i) => setInv({ ...inv, items: inv.items.filter((_, idx) => idx !== i) });

  const taxable = inv.items.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0), 0);
  const tax = inv.items.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0) * (parseFloat(l.gst_rate) || 0) / 100, 0);

  const save = async () => {
    if (!inv.party_ledger_id) { toast.error("Select a party ledger"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/acc/${cid}/invoice`, { ...inv, items: inv.items.map((l) => ({ ...l, qty: parseFloat(l.qty) || 0, rate: parseFloat(l.rate) || 0, gst_rate: parseFloat(l.gst_rate) || 0 })) });
      setResult(r.data.totals); toast.success("Invoice posted with double-entry + GST");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="acc-invoicing" className="max-w-3xl">
      <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <select data-testid="inv-type" value={inv.vtype} onChange={(e) => setInv({ ...inv, vtype: e.target.value, party_ledger_id: "" })} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
            <option>Sales</option><option>Purchase</option><option>Credit Note</option><option>Debit Note</option>
          </select>
          <input data-testid="inv-date" type="date" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
          <input data-testid="inv-number" placeholder="Invoice No." value={inv.number} onChange={(e) => setInv({ ...inv, number: e.target.value })} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
          <input data-testid="inv-state" placeholder="Party state" value={inv.party_state} onChange={(e) => setInv({ ...inv, party_state: e.target.value })} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
        </div>
        <select data-testid="inv-party" value={inv.party_ledger_id} onChange={(e) => setInv({ ...inv, party_ledger_id: e.target.value })} className="w-full mb-4 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
          <option value="">Select party ({partyGroup})…</option>
          {parties.map((l) => <option key={l.ledger_id} value={l.ledger_id}>{l.name}</option>)}
        </select>
        {parties.length === 0 && <p className="text-xs text-[#E09494] -mt-2 mb-3">No {partyGroup} ledger yet — add one in Chart of Accounts.</p>}

        <div className="space-y-2">
          {inv.items.map((l, i) => (
            <div key={l._uid} className="grid grid-cols-12 gap-2 items-center" data-testid={`inv-line-${i}`}>
              {items.length > 0 ? (
                <select value={l.item_id} onChange={(e) => pickItem(i, e.target.value)} className="col-span-4 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm outline-none">
                  <option value="">Free item…</option>{items.map((x) => <option key={x.item_id} value={x.item_id}>{x.name}</option>)}
                </select>
              ) : (
                <input placeholder="Item" value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} className="col-span-4 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm outline-none" />
              )}
              <input type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="col-span-2 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm text-right outline-none" />
              <input type="number" placeholder="Rate" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} className="col-span-3 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm text-right outline-none" />
              <select value={l.gst_rate} onChange={(e) => setLine(i, "gst_rate", e.target.value)} className="col-span-2 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-1 py-2 text-sm outline-none">
                {[0, 5, 12, 18, 28].map((g) => <option key={g} value={g}>{g}%</option>)}
              </select>
              <button onClick={() => delLine(i)} className="col-span-1 text-[#8C8381] hover:text-[#E09494]"><Trash size={14} /></button>
            </div>
          ))}
        </div>
        <button onClick={addLine} className="mt-2 text-sm text-[#F29A86] font-medium flex items-center gap-1"><Plus size={14} /> Add item</button>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#2A2625]/8 pt-4">
          <div className="text-sm text-[#8C8381]" data-testid="inv-summary">Taxable {inr(taxable)} · GST {inr(tax)} · <b className="text-[#2A2625]">Total {inr(taxable + tax)}</b></div>
          <button data-testid="post-invoice-btn" onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 disabled:opacity-60">{saving ? <CircleNotch size={16} className="animate-spin" /> : <Receipt size={16} weight="bold" />} Post invoice</button>
        </div>
      </div>
      {result && (
        <div className="mt-4 bg-[#F3FBF6] border border-emerald-200 rounded-2xl p-4 text-sm" data-testid="inv-result">
          <p className="flex items-center gap-2 text-emerald-700 font-medium mb-2"><CheckCircle size={18} weight="fill" /> Posted successfully ({result.intra ? "Intra-state CGST+SGST" : "Inter-state IGST"})</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[#2A2625]">
            <div>Taxable<br /><b>{inr(result.taxable)}</b></div><div>CGST<br /><b>{inr(result.cgst)}</b></div><div>SGST<br /><b>{inr(result.sgst)}</b></div><div>IGST<br /><b>{inr(result.igst)}</b></div><div>Total<br /><b>{inr(result.total)}</b></div>
          </div>
        </div>
      )}
    </div>
  );
}
